import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { OnChangeFn, VisibilityState } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { PositionStage, type Page, type PositionStage as PositionStageCode } from '@clawhire/shared';
import { useAuth } from '@/auth/AuthProvider';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { Board } from '@/components/data/Board';
import { DataTable } from '@/components/data/DataTable';
import { FilterBar } from '@/components/data/FilterBar';
import { ViewSwitcher } from '@/components/data/ViewSwitcher';
import { boardGroupsFor } from '@/components/data/tones';
import { useSavedViews } from '@/components/data/useSavedViews';
import { PositionCard } from '../components/PositionCard';
import { useInvalidatePositions, usePartners, usePositionsList, useUsersDirectory } from '../queries';
import type { PositionListItem } from '../types';
import { useStageChange } from '../useStageChange';
import { ALL_COLUMN_IDS, DEFAULT_COLUMNS, PINNED_COLUMNS, buildColumns } from './columns';
import { buildFilterDefs, toListQuery } from './filters';
import { PositionCreateDialog } from './PositionCreateDialog';

const STAGE_GROUPS = boardGroupsFor(PositionStage);

/**
 * `/positions`: saved views (Pipeline board, All positions table, Go/No-go queue …), filters that
 * map 1:1 to the list query, drag & drop stage changes with the closing dialog, "New position".
 */
export function PositionsList() {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const invalidate = useInvalidatePositions();
  const intermediaries = usePartners('INTERMEDIARY');
  const users = useUsersDirectory();
  const filterDefs = React.useMemo(
    () => buildFilterDefs(intermediaries.data ?? [], users.data ?? []),
    [intermediaries.data, users.data],
  );

  const sv = useSavedViews('POSITION', { defaults: { layout: 'TABLE' } });
  const query = React.useMemo(() => toListQuery(sv.state.filters), [sv.state.filters]);
  const listKey = queryKeys.list('positions', query);
  const list = usePositionsList(query);
  const items = React.useMemo(() => list.data?.items ?? [], [list.data]);
  const [createOpen, setCreateOpen] = React.useState(false);

  const patch = React.useCallback(
    async (id: string, body: Record<string, unknown>) => {
      try {
        await api.patch(`/positions/${encodeURIComponent(id)}`, body);
        toast.success('Saved');
        await invalidate();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Could not save');
        throw e;
      }
    },
    [invalidate],
  );
  const columns = React.useMemo(() => buildColumns({ canWrite, onPatch: patch }), [canWrite, patch]);

  const stage = useStageChange();
  const onMove = React.useCallback(
    async (id: string, _from: string, to: string) => {
      const target = to as PositionStageCode;
      if (!PositionStage.is(target)) return;
      if (target === 'LOST' || target === 'DROPPED') {
        stage.openClosing(id, target);
        return;
      }
      // Optimistic move; the server answer (or the closing dialog) is the source of truth.
      const previous = queryClient.getQueryData<Page<PositionListItem>>(listKey);
      queryClient.setQueryData<Page<PositionListItem>>(listKey, (old) =>
        old ? { ...old, items: old.items.map((p) => (p.id === id ? { ...p, stage: target } : p)) } : old,
      );
      try {
        const result = await stage.change(id, target);
        if (!result) queryClient.setQueryData(listKey, previous);
      } catch {
        queryClient.setQueryData(listKey, previous);
      }
    },
    [listKey, queryClient, stage],
  );

  // Saved-view columns <-> TanStack visibility state.
  const columnVisibility = React.useMemo<VisibilityState>(() => {
    const visible = new Set<string>(sv.state.columns.length ? sv.state.columns : DEFAULT_COLUMNS);
    for (const id of PINNED_COLUMNS) visible.add(id);
    const v: VisibilityState = {};
    for (const id of ALL_COLUMN_IDS) v[id] = visible.has(id);
    return v;
  }, [sv.state.columns]);
  const onColumnVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
    const next = typeof updater === 'function' ? updater(columnVisibility) : updater;
    sv.setColumns(ALL_COLUMN_IDS.filter((id) => next[id] !== false));
  };

  const total = list.data?.total ?? 0;
  const truncated = total > items.length;

  return (
    <>
      <PageHeader
        title="Positions"
        description="Job requests from the portal and direct clients."
        actions={
          canWrite ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden="true" /> New position
            </Button>
          ) : null
        }
      />
      <div className="space-y-3">
        <ViewSwitcher {...sv} filterDefs={filterDefs} canSave={canWrite} />
        <FilterBar filters={filterDefs} values={sv.state.filters} onChange={sv.setFilters} />

        {list.error ? (
          <EmptyState
            title="Could not load positions"
            description={list.error.message}
            action={
              <Button variant="outline" onClick={() => list.refetch()}>
                Retry
              </Button>
            }
          />
        ) : sv.state.layout === 'BOARD' ? (
          list.isLoading ? (
            <div className="flex gap-3 overflow-hidden" aria-busy="true">
              {STAGE_GROUPS.slice(0, 5).map((g) => (
                <Skeleton key={g.key} className="h-64 w-72 shrink-0" />
              ))}
            </div>
          ) : (
            <Board
              items={items}
              groups={STAGE_GROUPS}
              groupKey={(p) => p.stage}
              getItemId={(p) => p.id}
              renderCard={(p) => <PositionCard position={p} />}
              onMove={onMove}
              onCardClick={(p) => navigate(`/positions/${p.id}`)}
              readOnly={!canWrite || stage.isPending}
              label="Positions pipeline"
            />
          )
        ) : (
          <DataTable
            columns={columns}
            data={items}
            isLoading={list.isLoading}
            rowHref={(p) => `/positions/${p.id}`}
            sorting={sv.state.sorts}
            onSortingChange={(u) => sv.setSorts(typeof u === 'function' ? u(sv.state.sorts) : u)}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={onColumnVisibilityChange}
            hideSearch
            pageSize={50}
            emptyTitle="No positions match"
            emptyDescription="Change the filters or create a new position."
            caption="Positions"
          />
        )}

        {truncated ? (
          <p className="text-xs text-muted-foreground">
            Showing the first {items.length} of {total} positions – narrow the filters to see the rest.
          </p>
        ) : null}
      </div>

      {stage.dialog}
      <PositionCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
}
